import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getCampaignList } from "../apis/campaign";

/**
 * Loads paginated campaign cards for the Campaigns grid.
 * Single API call — lean payload from /api/campaigns/list.
 *
 * reload({ silent: true }) refreshes in the background without the full-page loader.
 */
export function useCampaignList({ page, perPage, filters }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pagination, setPagination] = useState({
    total_items: 0,
    total_pages: 0,
  });

  const load = useCallback(
    async (options = {}) => {
      const silent = Boolean(options.silent);

      if (silent) {
        setIsRefreshing(true);
      } else {
        setLoading(true);
      }

      try {
        const params = {
          page,
          limit: perPage,
        };

        if (filters.status) params.status = filters.status;
        if (filters.campaign_name) params.campaign_name = filters.campaign_name;
        if (filters.template_id) params.template_id = filters.template_id;
        if (filters.whatsapp_account_id) params.whatsapp_account_id = filters.whatsapp_account_id;
        if (filters.date_from) params.date_from = filters.date_from;
        if (filters.date_to) params.date_to = filters.date_to;

        const response = await getCampaignList(params);
        setCampaigns(response.data.data || []);

        if (response.data.pagination) {
          setPagination({
            total_items: response.data.pagination.total_items || 0,
            total_pages: response.data.pagination.total_pages || 0,
          });
        } else {
          setPagination({
            total_items: response.data.data?.length || 0,
            total_pages: 1,
          });
        }
      } catch (error) {
        console.error("[useCampaignList] load failed:", error);
        toast.error(error.response?.data?.error || "Failed to load campaigns");
      } finally {
        if (silent) {
          setIsRefreshing(false);
        } else {
          setLoading(false);
        }
      }
    },
    [
      page,
      perPage,
      filters.status,
      filters.campaign_name,
      filters.template_id,
      filters.whatsapp_account_id,
      filters.date_from,
      filters.date_to,
    ]
  );

  useEffect(() => {
    load();
  }, [load]);

  const updateCampaignStats = useCallback((campaignId, stats, patch = {}) => {
    setCampaigns((prev) =>
      prev.map((c) => (c.campaign_id === campaignId ? { ...c, stats, ...patch } : c))
    );
  }, []);

  return {
    campaigns,
    loading,
    isRefreshing,
    pagination,
    reload: load,
    updateCampaignStats,
  };
}
